import { useAuth } from '../contexts/AuthContext';
import { Role } from '@easydone/shared';

/**
 * Permission definitions by role
 */
const ROLE_PERMISSIONS: Record<Role, string[]> = {
  [Role.ORG_ADMIN]: ['*'], // All permissions

  [Role.CENTRAL_ENGINEER]: [
    'templates.create',
    'templates.edit',
    'templates.delete',
    'inspections.view_all',
    'reports.generate',
    'data.export',
    'users.manage',
    'sites.manage',
    'assets.manage',
  ],

  [Role.LOCAL_ENGINEER]: [
    'templates.view',
    'templates.edit_assigned',
    'inspections.view_site',
    'inspections.review',
    'reports.generate',
    'sites.view',
    'sites.edit_assigned',
    'assets.view',
    'assets.edit_assigned',
  ],

  [Role.TEAM_LEADER]: [
    'inspections.create',
    'inspections.view_assigned',
    'inspections.review',
    'templates.view',
    'sites.view',
    'assets.view',
  ],

  [Role.OPERATOR]: [
    'inspections.create',
    'inspections.edit_own',
    'inspections.complete',
    'assets.view',
    'templates.view',
    'sites.view',
  ],

  [Role.CLIENT_VIEWER]: [
    'reports.view_assigned',
    'sites.view_assigned',
    'inspections.view_completed',
  ],
};

/**
 * Hook to check if the current user has a specific permission
 */
export function usePermission(permission: string): boolean {
  const { userData } = useAuth();

  if (!userData) {
    return false;
  }

  const rolePermissions = ROLE_PERMISSIONS[userData.role];

  // Admins have all permissions
  if (rolePermissions.includes('*')) {
    return true;
  }

  return rolePermissions.includes(permission);
}

/**
 * Hook to check if the current user has ANY of the specified permissions
 */
export function useHasAnyPermission(permissions: string[]): boolean {
  const { userData } = useAuth();

  if (!userData) {
    return false;
  }

  const rolePermissions = ROLE_PERMISSIONS[userData.role];

  if (rolePermissions.includes('*')) {
    return true;
  }

  return permissions.some(permission => rolePermissions.includes(permission));
}

/**
 * Hook to check if the current user has ALL of the specified permissions
 */
export function useHasAllPermissions(permissions: string[]): boolean {
  const { userData } = useAuth();

  if (!userData) {
    return false;
  }

  const rolePermissions = ROLE_PERMISSIONS[userData.role];

  if (rolePermissions.includes('*')) {
    return true;
  }

  return permissions.every(permission => rolePermissions.includes(permission));
}

/**
 * Hook to check if current user has a specific role
 */
export function useHasRole(role: Role): boolean {
  const { userData } = useAuth();
  return userData?.role === role;
}

/**
 * Hook to check if current user is an admin
 */
export function useIsAdmin(): boolean {
  return useHasRole(Role.ORG_ADMIN);
}
